import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CreditCard, ArrowRight, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "First step" CTA shown on the parent dashboard when no payment method
 * is on file. Quietly hides itself once the parent has saved a card.
 */
export default function FirstStepsCard() {
    const [state, setState] = useState({ loading: true, has_pm: false });

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/billing/me`, { withCredentials: true })
            .then((r) => {
                if (!cancelled) {
                    setState({ loading: false, has_pm: !!r.data?.has_payment_method });
                }
            })
            .catch(() => {
                if (!cancelled) setState({ loading: false, has_pm: false });
            });
        return () => { cancelled = true; };
    }, []);

    if (state.loading) {
        return (
            <div data-testid="first-steps-loading" className="border border-zinc-900 bg-zinc-950 p-5 rounded-lg flex items-center gap-3 text-zinc-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking your account…
            </div>
        );
    }
    if (state.has_pm) return null;

    return (
        <div
            data-testid="first-steps-card"
            className="relative border border-brand-red/40 bg-gradient-to-br from-brand-red/10 via-zinc-950 to-zinc-950 p-6 md:p-7 rounded-lg"
        >
            <div className="absolute top-0 right-0 bg-brand-red text-white text-[9px] tracking-[0.3em] uppercase px-2 py-0.5 rounded-bl">
                Start here
            </div>
            <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-brand-red/15 border border-brand-red/30 shrink-0">
                    <CreditCard className="w-6 h-6 text-brand-red" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">First thing to do</div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-wider uppercase text-white mt-1">
                        Add a payment method
                    </h3>
                    <p className="text-sm text-zinc-300 mt-3 max-w-lg leading-relaxed">
                        Save a card on file so your coach can charge you for sessions, packs, and your membership without
                        re-asking for billing info each time. You won't be charged until you specifically approve it.
                    </p>
                    <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
                        <Link
                            to="/dashboard/billing"
                            data-testid="first-steps-add-card-btn"
                            className="btn-tactical inline-flex items-center gap-2"
                        >
                            <CreditCard className="w-4 h-4" /> Add payment method
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <div className="text-[11px] text-zinc-400 inline-flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                            Secured by Stripe. We never see your card number.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
